import { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker.mjs";
import { usePdfCache } from "../../../contexts/PdfCacheContext";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  "https://unpkg.com/pdfjs-dist@4.7.76/build/pdf.worker.min.js";

export default function PdfThumbnail({ url }) {
  const canvasRef = useRef(null);
  const { addToCache, getFromCache, isInCache } = usePdfCache();
  const [isLoading, setIsLoading] = useState(true);
  const [cachedImage, setCachedImage] = useState(null);

  useEffect(() => {
    let isCancelled = false;

    // Check if the PDF is already in the cache
    if (isInCache(url)) {
      setCachedImage(getFromCache(url));
      setIsLoading(false);
      return;
    }

    // If not in cache, render it
    (async () => {
      try {
        setIsLoading(true);
        const pdf = await pdfjsLib.getDocument(url).promise;
        const page = await pdf.getPage(1); // first page only
        const viewport = page.getViewport({ scale: 0.2 }); // smaller size

        const canvas = canvasRef.current;
        if (!canvas || isCancelled) return;

        const ctx = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: ctx, viewport }).promise;
        
        // Cache the rendered PDF as a data URL
        const dataUrl = canvas.toDataURL('image/png');
        addToCache(url, dataUrl);
        setCachedImage(dataUrl);
        setIsLoading(false);
      } catch (err) {
        console.error("Error rendering PDF thumbnail:", err);
        setIsLoading(false);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [url, addToCache, getFromCache, isInCache]);

  // If we have a cached image, display it directly
  if (cachedImage) {
    return (
      <img 
        src={cachedImage} 
        alt="PDF Thumbnail" 
        style={{ width: "110px", height: "110px", borderRadius: "5px", objectFit: "cover" }}
      />
    );
  }

  // Show loading state or canvas for initial rendering
  return (
    <>
      {isLoading && (
        <div 
          style={{ 
            width: "110px", 
            height: "110px", 
            borderRadius: "5px", 
            backgroundColor: "#f5f5f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "12px",
            color: "#666"
          }}
        >
          Chargement...
        </div>
      )}
      <canvas
        ref={canvasRef}
        style={{ 
          width: "110px", 
          height: "110px", 
          borderRadius: "5px",
          display: isLoading ? "none" : "block" 
        }}
      />
    </>
  );
}
